import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Shield, Globe, AlertCircle, Key, Copy, CheckCircle2, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'AED', 'SGD'];

const Settings = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [twofaSetup, setTwofaSetup] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [twofaEnabled, setTwofaEnabled] = useState(user?.twofa_enabled || false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings`);
      setSettings(r.data);
      setDraft(r.data);
    } catch (e) {
      console.warn('[Settings] failed to load settings', e?.response?.status);
    }
    try {
      const r = await axios.get(`${API}/auth/me`);
      setTwofaEnabled(!!r.data?.twofa_enabled);
    } catch (e) {
      console.warn('[Settings] failed to refresh /auth/me', e?.response?.status);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.put(`${API}/settings`, draft);
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    }
  };

  const handle2FASetup = async () => {
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`);
      setTwofaSetup(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start 2FA setup');
    }
  };

  const handle2FAVerify = async () => {
    try {
      const res = await axios.post(`${API}/auth/2fa/verify`, { code: verifyCode });
      setRecoveryCodes(res.data.recovery_codes);
      setTwofaEnabled(true);
      setTwofaSetup(null);
      setVerifyCode('');
      toast.success('2FA enabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    }
  };

  const handle2FADisable = async () => {
    try {
      if (user?.auth_provider === 'google') {
        await axios.post(`${API}/auth/2fa/disable-google`, { twofa_code: disablePassword });
      } else {
        await axios.post(`${API}/auth/2fa/disable`, { password: disablePassword });
      }
      setTwofaEnabled(false);
      setDisablePassword('');
      setRecoveryCodes([]);
      toast.success('2FA disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disable');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied'));
  };

  if (!draft) return <div className="p-8 text-slate-900 font-ibm">Loading...</div>;

  return (
    <div className="p-6 md:p-12" data-testid="settings-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8">
          <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="settings-title">Settings</h1>
          <p className="text-slate-600 mt-1 font-ibm">App configuration & security</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="general-settings-card">
            <div className="flex items-center gap-2 mb-4">
              <Globe size={18} className="text-slate-600" />
              <h2 className="text-lg font-chivo font-bold text-slate-900">General</h2>
            </div>
            {!isAdmin && (
              <div className="text-xs font-ibm text-slate-500 mb-3 flex items-center gap-1">
                <AlertCircle size={14} /> Read-only — admin access required to modify
              </div>
            )}
            <div className="space-y-4">
              <div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Base Currency</Label>
                <Select value={draft.base_currency} onValueChange={(v) => setDraft({ ...draft, base_currency: v })} disabled={!isAdmin}>
                  <SelectTrigger data-testid="setting-base-currency" className="mt-2 bg-slate-50 border-slate-200 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Warning %</Label>
                  <Input
                    data-testid="setting-warning-threshold"
                    type="number" min="1" max="100"
                    value={draft.budget_warning_threshold}
                    onChange={(e) => setDraft({ ...draft, budget_warning_threshold: parseFloat(e.target.value) })}
                    disabled={!isAdmin}
                    className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Over %</Label>
                  <Input
                    data-testid="setting-over-threshold"
                    type="number" min="1" max="200"
                    value={draft.budget_over_threshold}
                    onChange={(e) => setDraft({ ...draft, budget_over_threshold: parseFloat(e.target.value) })}
                    disabled={!isAdmin}
                    className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Recur. Min</Label>
                  <Input
                    data-testid="setting-recurrence-interval"
                    type="number" min="1" max="1440"
                    value={draft.recurrence_interval_minutes}
                    onChange={(e) => setDraft({ ...draft, recurrence_interval_minutes: parseInt(e.target.value) })}
                    disabled={!isAdmin}
                    className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Alert Min</Label>
                  <Input
                    data-testid="setting-alert-interval"
                    type="number" min="1" max="1440"
                    value={draft.alert_interval_minutes}
                    onChange={(e) => setDraft({ ...draft, alert_interval_minutes: parseInt(e.target.value) })}
                    disabled={!isAdmin}
                    className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                  />
                </div>
              </div>
                <div>
                  <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                    Alert Cooldown (min)
                  </Label>
                  <Input
                    data-testid="setting-alert-cooldown"
                    type="number" min="1" max="10080"
                    value={draft.alert_cooldown_minutes || 1440}
                    onChange={(e) => setDraft({ ...draft, alert_cooldown_minutes: parseInt(e.target.value) })}
                    disabled={!isAdmin}
                    className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                  />
                  <p className="text-xs text-slate-500 font-ibm mt-1">
                    Minimum minutes between alerts for the same budget category
                  </p>
                </div>
              {isAdmin && (
                <Button
                  onClick={handleSave}
                  data-testid="save-settings-button"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                >
                  Save Settings
                </Button>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="twofa-settings-card">
            <div className="flex items-center gap-2 mb-4">
              <Shield size={18} className="text-slate-600" />
              <h2 className="text-lg font-chivo font-bold text-slate-900">Two-Factor Authentication</h2>
            </div>
            {twofaEnabled ? (
              <div data-testid="twofa-enabled-section">
                <div className="flex items-center gap-2 mb-3 text-emerald-700">
                  <CheckCircle2 size={16} /> <span className="text-sm font-ibm font-semibold">2FA is enabled</span>
                </div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                  Disable 2FA — Confirm {user?.auth_provider === 'google' ? '6-digit code' : 'password'}
                </Label>
                <Input
                  data-testid="twofa-disable-password"
                  type={user?.auth_provider === 'google' ? 'text' : 'password'}
                  inputMode={user?.auth_provider === 'google' ? 'numeric' : undefined}
                  maxLength={user?.auth_provider === 'google' ? 6 : undefined}
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                />
                <Button
                  onClick={handle2FADisable}
                  data-testid="twofa-disable-button"
                  className="w-full mt-3 bg-red-600 hover:bg-red-700 text-white rounded-sm"
                >
                  Disable 2FA
                </Button>
              </div>
            ) : !twofaSetup ? (
              <div>
                <p className="text-sm font-ibm text-slate-600 mb-3">
                  Add an extra layer of security. Scan a QR code with your authenticator app, then verify with a 6-digit code.
                </p>
                <Button
                  onClick={handle2FASetup}
                  data-testid="twofa-start-setup"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                >
                  <Key size={14} className="mr-2" /> Enable 2FA
                </Button>
              </div>
            ) : (
              <div data-testid="twofa-setup-section">
                <p className="text-sm font-ibm text-slate-600 mb-3">Scan with Google Authenticator / Authy / 1Password:</p>
                <div className="flex justify-center mb-3">
                  <img
                    src={`data:image/png;base64,${twofaSetup.qr_code_base64}`}
                    alt="2FA QR code"
                    data-testid="twofa-qr-image"
                    className="border border-slate-200 rounded-sm"
                  />
                </div>
                <div className="text-xs font-ibm text-slate-500 mb-3 text-center break-all">
                  Or manually: <code className="font-mono">{twofaSetup.secret}</code>
                </div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                  Verify with 6-digit code
                </Label>
                <Input
                  data-testid="twofa-verify-input"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  className="mt-2 bg-slate-50 border-slate-200 rounded-sm tracking-widest text-center"
                />
                <Button
                  onClick={handle2FAVerify}
                  data-testid="twofa-verify-button"
                  className="w-full mt-3 bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                >
                  Verify & Enable
                </Button>
              </div>
            )}

            {recoveryCodes.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="recovery-codes-card">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-ibm font-semibold uppercase tracking-widest text-amber-800">
                    Recovery Codes — save these now
                  </p>
                  <button
                    onClick={() => copyToClipboard(recoveryCodes.join('\n'))}
                    className="text-amber-700 hover:text-amber-900"
                    data-testid="copy-recovery-codes"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs font-mono text-amber-900">
                  {recoveryCodes.map((c) => <code key={c}>{c}</code>)}
                </div>
              </div>
            )}
          </div>

          {/* Change Password */}
          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="change-password-card">
            <div className="flex items-center gap-2 mb-4">
              <KeyRound size={18} className="text-slate-700" />
              <h2 className="text-lg font-chivo font-bold text-slate-900">Change Password</h2>
            </div>
            <p className="text-sm font-ibm text-slate-600 mb-4">
              Pick a new password — at least 8 characters. You&apos;ll stay signed in after the change.
            </p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (pwNew !== pwConfirm) {
                  toast.error('New password and confirmation do not match');
                  return;
                }
                if (pwNew.length < 8) {
                  toast.error('New password must be at least 8 characters');
                  return;
                }
                setPwSaving(true);
                try {
                  await axios.post(`${API}/auth/change-password`, {
                    current_password: pwCurrent,
                    new_password: pwNew,
                  });
                  toast.success('Password changed');
                  setPwCurrent(''); setPwNew(''); setPwConfirm('');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Could not change password');
                } finally {
                  setPwSaving(false);
                }
              }}
              className="grid grid-cols-1 md:grid-cols-3 gap-3"
              data-testid="change-password-form"
            >
              <div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Current</Label>
                <Input
                  data-testid="cp-current"
                  type="password"
                  required
                  value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)}
                  className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">New</Label>
                <Input
                  data-testid="cp-new"
                  type="password"
                  required
                  minLength={8}
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Confirm</Label>
                <Input
                  data-testid="cp-confirm"
                  type="password"
                  required
                  minLength={8}
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                />
              </div>
              <div className="md:col-span-3">
                <Button
                  type="submit"
                  data-testid="cp-submit"
                  disabled={pwSaving}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                >
                  {pwSaving ? 'Saving…' : 'Change Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Settings;
